'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────
interface StateCapacityRule {
  warn: number;
  critical: number;
  enabled: boolean;
}

interface DocExpiryRule {
  warn_days: number;
  critical_days: number;
  enabled: boolean;
}

interface MetricTriggerRule {
  warn: number;
  critical: number;
  enabled: boolean;
}

interface ChannelConfig {
  in_app: boolean;
  email: boolean;
  sms: boolean;
}

interface AlertConfig {
  alert_type: string;
  thresholds: Record<string, any>;
  channels: ChannelConfig;
  notify_roles: string[];
  is_enabled: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const ALL_ROLES = ['Admin', 'Manager', 'Sales'];

const STATES = ['OH', 'TX', 'CA', 'FL', 'NY', 'GA', 'IL', 'PA', 'NC', 'AZ'];
const DOC_TYPES = ['General Liability', 'Workers Comp', 'W9'];
const PERF_METRICS = [
  { key: 'on_time_arrival', label: 'On-Time Arrival' },
  { key: 'on_time_completion', label: 'On-Time Completion' },
  { key: 'at_cost_completion', label: 'At-Cost Completion' },
];

const DEFAULT_STATE_RULE: StateCapacityRule = { warn: 80, critical: 95, enabled: true };
const DEFAULT_DOC_RULE: DocExpiryRule = { warn_days: 30, critical_days: 7, enabled: true };
const DEFAULT_METRIC_RULE: MetricTriggerRule = { warn: 85, critical: 75, enabled: true };
const DEFAULT_CHANNELS: ChannelConfig = { in_app: true, email: false, sms: false };

// ─── Toggle Switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? 'bg-cyan-500' : 'bg-gray-200'}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

// ─── Number Input ─────────────────────────────────────────────────────────────
function NumInput({ value, onChange, min = 0, max = 100, suffix = '%', disabled = false }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; suffix?: string; disabled?: boolean;
}) {
  return (
    <div className={`flex items-center gap-1 ${disabled ? 'opacity-40' : ''}`}>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={e => onChange(Math.min(max, Math.max(min, Number(e.target.value))))}
        className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
      />
      {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, description, icon, children }: {
  title: string; description: string; icon: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" style={{ borderRadius: '12px' }}>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E89A3C18 0%, #1C2E5E18 100%)' }}>
          <Icon name={icon as any} size={18} style={{ color: '#E89A3C' }} variant="solid" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AlertConfigTab() {
  const supabase = createClient();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });

  // ── Crew Capacity State ──────────────────────────────────────────────────────
  const [capacityRules, setCapacityRules] = useState<Record<string, StateCapacityRule>>(
    Object.fromEntries(STATES.map(s => [s, { ...DEFAULT_STATE_RULE }]))
  );
  const [capacityChannels, setCapacityChannels] = useState<ChannelConfig>({ ...DEFAULT_CHANNELS, email: true });
  const [capacityRoles, setCapacityRoles] = useState<string[]>(['Admin', 'Manager']);
  const [capacityEnabled, setCapacityEnabled] = useState(true);

  // ── Compliance Expiry State ──────────────────────────────────────────────────
  const [docRules, setDocRules] = useState<Record<string, DocExpiryRule>>(
    Object.fromEntries(DOC_TYPES.map(d => [d, { ...DEFAULT_DOC_RULE }]))
  );
  const [docChannels, setDocChannels] = useState<ChannelConfig>({ ...DEFAULT_CHANNELS, email: true });
  const [docRoles, setDocRoles] = useState<string[]>(['Admin', 'Manager']);
  const [docEnabled, setDocEnabled] = useState(true);

  // ── Performance Trend State ──────────────────────────────────────────────────
  const [metricRules, setMetricRules] = useState<Record<string, MetricTriggerRule>>(
    Object.fromEntries(PERF_METRICS.map(m => [m.key, { ...DEFAULT_METRIC_RULE }]))
  );
  const [metricChannels, setMetricChannels] = useState<ChannelConfig>({ ...DEFAULT_CHANNELS });
  const [metricRoles, setMetricRoles] = useState<string[]>(['Admin', 'Manager']);
  const [metricEnabled, setMetricEnabled] = useState(true);

  // ── Load from Supabase ───────────────────────────────────────────────────────
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('alert_configurations')
        .select('*');
      if (error) throw error;
      if (!data) return;

      data.forEach((row: AlertConfig) => {
        if (row.alert_type === 'crew_capacity_warnings') {
          const states = row.thresholds?.states || {};
          setCapacityRules(prev => {
            const merged = { ...prev };
            Object.entries(states).forEach(([st, rule]: [string, any]) => {
              merged[st] = { warn: rule.warn ?? 80, critical: rule.critical ?? 95, enabled: rule.enabled ?? true };
            });
            return merged;
          });
          setCapacityChannels(row.channels || DEFAULT_CHANNELS);
          setCapacityRoles(row.notify_roles || []);
          setCapacityEnabled(row.is_enabled ?? true);
        } else if (row.alert_type === 'compliance_expiry_alerts') {
          const docs = row.thresholds?.documents || {};
          setDocRules(prev => {
            const merged = { ...prev };
            Object.entries(docs).forEach(([doc, rule]: [string, any]) => {
              merged[doc] = { warn_days: rule.warn_days ?? 30, critical_days: rule.critical_days ?? 7, enabled: rule.enabled ?? true };
            });
            return merged;
          });
          setDocChannels(row.channels || DEFAULT_CHANNELS);
          setDocRoles(row.notify_roles || []);
          setDocEnabled(row.is_enabled ?? true);
        } else if (row.alert_type === 'performance_trend_triggers') {
          const metrics = row.thresholds?.metrics || {};
          setMetricRules(prev => {
            const merged = { ...prev };
            Object.entries(metrics).forEach(([key, rule]: [string, any]) => {
              merged[key] = { warn: rule.warn ?? 85, critical: rule.critical ?? 75, enabled: rule.enabled ?? true };
            });
            return merged;
          });
          setMetricChannels(row.channels || DEFAULT_CHANNELS);
          setMetricRoles(row.notify_roles || []);
          setMetricEnabled(row.is_enabled ?? true);
        }
      });
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  // ── Save to Supabase ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const updatedBy = currentUser?.email || 'system';

      const configs = [
        {
          alert_type: 'crew_capacity_warnings',
          thresholds: { states: capacityRules },
          channels: capacityChannels,
          notify_roles: capacityRoles,
          is_enabled: capacityEnabled,
          updated_by: updatedBy,
          updated_at: now,
        },
        {
          alert_type: 'compliance_expiry_alerts',
          thresholds: { documents: docRules },
          channels: docChannels,
          notify_roles: docRoles,
          is_enabled: docEnabled,
          updated_by: updatedBy,
          updated_at: now,
        },
        {
          alert_type: 'performance_trend_triggers',
          thresholds: { metrics: metricRules },
          channels: metricChannels,
          notify_roles: metricRoles,
          is_enabled: metricEnabled,
          updated_by: updatedBy,
          updated_at: now,
        },
      ];

      for (const config of configs) {
        const { error } = await supabase
          .from('alert_configurations')
          .upsert(config, { onConflict: 'alert_type' });
        if (error) throw error;
      }

      setToast({ show: true, message: 'Alert configuration saved successfully!', type: 'success' });
      setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
    } catch (err: any) {
      setToast({ show: true, message: err?.message || 'Failed to save configuration.', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Role Toggle Helper ────────────────────────────────────────────────────────
  const toggleRole = (roles: string[], role: string, setRoles: (r: string[]) => void) => {
    setRoles(roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role]);
  };

  // ── Channel Row ───────────────────────────────────────────────────────────────
  const ChannelRow = ({ channels, setChannels }: { channels: ChannelConfig; setChannels: (c: ChannelConfig) => void }) => (
    <div className="flex flex-wrap gap-4 mt-1">
      {[
        { key: 'in_app' as const, label: 'In-App', icon: 'BellIcon' },
        { key: 'email' as const, label: 'Email', icon: 'EnvelopeIcon' },
        { key: 'sms' as const, label: 'SMS', icon: 'DevicePhoneMobileIcon' },
      ].map(ch => (
        <label key={ch.key} className="flex items-center gap-2 cursor-pointer select-none">
          <Toggle
            checked={channels[ch.key]}
            onChange={v => setChannels({ ...channels, [ch.key]: v })}
            label={ch.label}
          />
          <Icon name={ch.icon as any} size={14} className="text-gray-500" />
          <span className="text-sm text-gray-700">{ch.label}</span>
        </label>
      ))}
    </div>
  );

  // ── Roles Multi-Select ────────────────────────────────────────────────────────
  const RolesSelect = ({ roles, setRoles }: { roles: string[]; setRoles: (r: string[]) => void }) => (
    <div className="flex flex-wrap gap-2 mt-1">
      {ALL_ROLES.map(role => (
        <button
          key={role}
          type="button"
          onClick={() => toggleRole(roles, role, setRoles)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
            roles.includes(role)
              ? 'border-cyan-400 bg-cyan-50 text-cyan-700' :'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
          }`}
        >
          {role.replace(/_/g, ' ')}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">

      {/* ── 1. Crew Capacity Warnings ─────────────────────────────────────────── */}
      <SectionCard
        title="Crew Capacity Warnings"
        description="Trigger alerts when a state's crew capacity exceeds configured thresholds"
        icon="ExclamationTriangleIcon"
      >
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-medium text-gray-700">Enable Crew Capacity Alerts</span>
          <Toggle checked={capacityEnabled} onChange={setCapacityEnabled} label="Enable crew capacity alerts" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">State</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Warn Threshold</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Critical Threshold</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {STATES.map(state => {
                const rule = capacityRules[state] || { ...DEFAULT_STATE_RULE };
                return (
                  <tr key={state} className={`${!capacityEnabled ? 'opacity-50' : ''}`}>
                    <td className="py-3 pr-4">
                      <span className="inline-flex items-center justify-center w-10 h-7 rounded-md text-xs font-bold text-white" style={{ background: '#E89A3C' }}>
                        {state}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                        <NumInput
                          value={rule.warn}
                          onChange={v => setCapacityRules(prev => ({ ...prev, [state]: { ...rule, warn: v } }))}
                          disabled={!capacityEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <NumInput
                          value={rule.critical}
                          onChange={v => setCapacityRules(prev => ({ ...prev, [state]: { ...rule, critical: v } }))}
                          disabled={!capacityEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Toggle
                        checked={rule.enabled}
                        onChange={v => setCapacityRules(prev => ({ ...prev, [state]: { ...rule, enabled: v } }))}
                        label={`Enable ${state} rule`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Channels */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notification Channels</p>
          <ChannelRow channels={capacityChannels} setChannels={setCapacityChannels} />
        </div>

        {/* Roles */}
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notify Roles</p>
          <RolesSelect roles={capacityRoles} setRoles={setCapacityRoles} />
        </div>
      </SectionCard>

      {/* ── 2. Compliance Expiry Alerts ───────────────────────────────────────── */}
      <SectionCard
        title="Compliance Expiry Alerts"
        description="Alert when crew documents are approaching or past their expiration date"
        icon="ShieldExclamationIcon"
      >
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-medium text-gray-700">Enable Compliance Expiry Alerts</span>
          <Toggle checked={docEnabled} onChange={setDocEnabled} label="Enable compliance expiry alerts" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Document Type</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Warn (days before)</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Critical (days before)</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {DOC_TYPES.map(doc => {
                const rule = docRules[doc] || { ...DEFAULT_DOC_RULE };
                return (
                  <tr key={doc} className={`${!docEnabled ? 'opacity-50' : ''}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Icon name="DocumentTextIcon" size={14} className="text-blue-500" />
                        </div>
                        <span className="font-medium text-gray-800 text-sm">{doc}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                        <NumInput
                          value={rule.warn_days}
                          onChange={v => setDocRules(prev => ({ ...prev, [doc]: { ...rule, warn_days: v } }))}
                          min={1}
                          max={365}
                          suffix="days"
                          disabled={!docEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <NumInput
                          value={rule.critical_days}
                          onChange={v => setDocRules(prev => ({ ...prev, [doc]: { ...rule, critical_days: v } }))}
                          min={1}
                          max={365}
                          suffix="days"
                          disabled={!docEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Toggle
                        checked={rule.enabled}
                        onChange={v => setDocRules(prev => ({ ...prev, [doc]: { ...rule, enabled: v } }))}
                        label={`Enable ${doc} rule`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Channels */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notification Channels</p>
          <ChannelRow channels={docChannels} setChannels={setDocChannels} />
        </div>

        {/* Roles */}
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notify Roles</p>
          <RolesSelect roles={docRoles} setRoles={setDocRoles} />
        </div>
      </SectionCard>

      {/* ── 3. Performance Trend Triggers ────────────────────────────────────── */}
      <SectionCard
        title="Performance Trend Triggers"
        description="Fire an alert when a crew's performance metric drops below the configured percentage"
        icon="ArrowTrendingDownIcon"
      >
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-medium text-gray-700">Enable Performance Trend Alerts</span>
          <Toggle checked={metricEnabled} onChange={setMetricEnabled} label="Enable performance trend alerts" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Warn Below</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Critical Below</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {PERF_METRICS.map(metric => {
                const rule = metricRules[metric.key] || { ...DEFAULT_METRIC_RULE };
                return (
                  <tr key={metric.key} className={`${!metricEnabled ? 'opacity-50' : ''}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#E89A3C18' }}>
                          <Icon name="ChartBarIcon" size={14} style={{ color: '#E89A3C' }} />
                        </div>
                        <span className="font-medium text-gray-800 text-sm">{metric.label}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                        <NumInput
                          value={rule.warn}
                          onChange={v => setMetricRules(prev => ({ ...prev, [metric.key]: { ...rule, warn: v } }))}
                          disabled={!metricEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <NumInput
                          value={rule.critical}
                          onChange={v => setMetricRules(prev => ({ ...prev, [metric.key]: { ...rule, critical: v } }))}
                          disabled={!metricEnabled || !rule.enabled}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Toggle
                        checked={rule.enabled}
                        onChange={v => setMetricRules(prev => ({ ...prev, [metric.key]: { ...rule, enabled: v } }))}
                        label={`Enable ${metric.label} rule`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Channels */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notification Channels</p>
          <ChannelRow channels={metricChannels} setChannels={setMetricChannels} />
        </div>

        {/* Roles */}
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notify Roles</p>
          <RolesSelect roles={metricRoles} setRoles={setMetricRoles} />
        </div>
      </SectionCard>

      {/* ── 4. Multi-Channel Routing Summary ─────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" style={{ borderRadius: '12px' }}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #E89A3C18 0%, #1C2E5E18 100%)' }}>
            <Icon name="SignalIcon" size={18} style={{ color: '#E89A3C' }} variant="solid" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Multi-Channel Routing Summary</h3>
            <p className="text-xs text-gray-500 mt-0.5">Overview of active channels per alert rule</p>
          </div>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Alert Rule</th>
                  <th className="text-center py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="BellIcon" size={13} className="text-gray-400" />
                      <span>In-App</span>
                    </div>
                  </th>
                  <th className="text-center py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="EnvelopeIcon" size={13} className="text-gray-400" />
                      <span>Email</span>
                    </div>
                  </th>
                  <th className="text-center py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="DevicePhoneMobileIcon" size={13} className="text-gray-400" />
                      <span>SMS</span>
                    </div>
                  </th>
                  <th className="text-left py-2 pl-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Roles Notified</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[
                  { label: 'Crew Capacity Warnings', channels: capacityChannels, roles: capacityRoles, enabled: capacityEnabled },
                  { label: 'Compliance Expiry Alerts', channels: docChannels, roles: docRoles, enabled: docEnabled },
                  { label: 'Performance Trend Triggers', channels: metricChannels, roles: metricRoles, enabled: metricEnabled },
                ].map(row => (
                  <tr key={row.label} className={`${!row.enabled ? 'opacity-40' : ''}`}>
                    <td className="py-3 pr-6">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${row.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span className="font-medium text-gray-800">{row.label}</span>
                      </div>
                    </td>
                    {(['in_app', 'email', 'sms'] as const).map(ch => (
                      <td key={ch} className="py-3 px-4 text-center">
                        {row.channels[ch]
                          ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-50"><Icon name="CheckIcon" size={12} className="text-cyan-600" /></span>
                          : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-50"><Icon name="MinusIcon" size={12} className="text-gray-300" /></span>
                        }
                      </td>
                    ))}
                    <td className="py-3 pl-4">
                      <div className="flex flex-wrap gap-1">
                        {row.roles.length === 0
                          ? <span className="text-xs text-gray-300">None selected</span>
                          : row.roles.slice(0, 3).map(r => (
                              <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{r.replace(/_/g, ' ')}</span>
                            ))
                        }
                        {row.roles.length > 3 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">+{row.roles.length - 3} more</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Save Button ───────────────────────────────────────────────────────── */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2.5 px-7 py-3 text-sm font-semibold text-white rounded-xl transition-all hover:opacity-90 active:scale-95 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: saving ? '#E89A3Caa' : '#E89A3C' }}
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving…</span>
            </>
          ) : (
            <>
              <Icon name="CheckCircleIcon" size={18} className="text-white" variant="solid" />
              <span>Save Configuration</span>
            </>
          )}
        </button>
      </div>

      {/* ── Success / Error Toast ─────────────────────────────────────────────── */}
      {toast.show && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-white text-sm font-medium transition-all animate-in slide-in-from-bottom-4 ${
            toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
          }`}
        >
          <Icon
            name={toast.type === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'}
            size={20}
            className="text-white flex-shrink-0"
            variant="solid"
          />
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(t => ({ ...t, show: false }))}
            className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
          >
            <Icon name="XMarkIcon" size={16} className="text-white" />
          </button>
        </div>
      )}
    </div>
  );
}
